// Live dashboard showing per-question error rates and attendee scores.
// Pure display component — reads data from the Zustand store only.
// The parent component is responsible for loading attendees and managing
// the realtime subscription so this component never makes direct API calls.
import { useMemo } from 'react'
import { Users, CheckCircle2, BarChart3, AlertTriangle } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/card'
import { useClassSessionStore, getCoursePassingScore, getCourseQuestions } from '@/features/classes/stores'
import type { ClassSession, SessionAttendee, Student } from '@/features/classes/stores'
import { cn } from '@/core/lib/utils'

import { AttendeeStatusBadge } from './StatusBadges'

export function RealtimeDashboard({ session }: { session: ClassSession }) {
  // Subscribe directly to attendees + students so this component re-renders
  // whenever the store is updated by loadSessionAttendees() (initial load or
  // real-time refresh).  Subscribing to getSessionAttendees (a stable fn ref)
  // would NOT trigger re-renders when the underlying data changes.
  // Raw array references only change when the store is updated with new data,
  // so this avoids unnecessary re-renders on unrelated store changes.
  const rawAttendees = useClassSessionStore((s) => s.attendees)
  const rawStudents = useClassSessionStore((s) => s.students)
  const attendees = useMemo((): Array<SessionAttendee & { student: Student }> =>
    rawAttendees
      .filter((a) => a.sessionId === session.id)
      .map((a) => ({ ...a, student: rawStudents.find((st) => st.id === a.studentId)! }))
      .filter((a) => a.student),
  [rawAttendees, rawStudents, session.id])
  const courses = useClassSessionStore((s) => s.courses)
  const course = courses.find((c) => c.slug === session.courseId)
  const passingScore = getCoursePassingScore(course)

  const withScore = attendees.filter((a) => a.quizScore !== null)
  const avgScore =
    withScore.length > 0
      ? Math.round(withScore.reduce((sum, a) => sum + (a.quizScore ?? 0), 0) / withScore.length)
      : null

  // Per-question error rates
  const questionStats = getCourseQuestions(course).map((q) => {
    const answered = attendees.filter((a) => a.quizAnswers[q.id] !== undefined)
    const wrong = answered.filter((a) => a.quizAnswers[q.id] !== q.correctIndex)
    return {
      question: q,
      answered: answered.length,
      errorRate: answered.length > 0 ? Math.round((wrong.length / answered.length) * 100) : 0,
    }
  })

  const sortedByError = [...questionStats].sort((a, b) => b.errorRate - a.errorRate)

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Registered', value: attendees.length, icon: Users, colorClass: 'text-navy' },
          { label: 'Diagnostic', value: withScore.length, icon: CheckCircle2, colorClass: 'text-green-500' },
          { label: 'Avg Score', value: avgScore !== null ? `${avgScore}%` : '—', icon: BarChart3, colorClass: 'text-primary' },
          { label: `Below ${passingScore}%`, value: withScore.filter((a) => (a.quizScore ?? 0) < passingScore).length, icon: AlertTriangle, colorClass: 'text-red-500' },
        ].map(({ label, value, icon: Icon, colorClass }) => (
          <Card key={label}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-500">{label}</span>
                <Icon className={cn('w-4 h-4', colorClass)} />
              </div>
              <p className={cn('text-2xl font-bold', colorClass)}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Per-question error rates */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-navy">Error Rate by Question</CardTitle>
          <CardDescription>Questions with higher errors need more attention during class</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {sortedByError.map(({ question, answered, errorRate }) => (
            <div key={question.id}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-sm text-navy flex-1 min-w-0 truncate">{question.text}</p>
                <span className={cn('text-sm font-semibold shrink-0', errorRate >= 60 ? 'text-red-600' : errorRate >= 30 ? 'text-yellow-600' : 'text-green-600')}>
                  {answered > 0 ? `${errorRate}% errors` : 'No answers yet'}
                </span>
              </div>
              <div className="w-full bg-gray-50 rounded-full h-2">
                {/* Inline width is dynamic (percentage) and cannot be expressed with static Tailwind classes */}
                <div
                  className={cn('h-2 rounded-full transition-all', errorRate >= 60 ? 'bg-red-500' : errorRate >= 30 ? 'bg-yellow-500' : 'bg-green-500')}
                  style={{ width: `${errorRate}%` }}
                />
              </div>
            </div>
          ))}
          {withScore.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">
              Waiting for students to complete the diagnostic…
            </p>
          )}
        </CardContent>
      </Card>

      {/* Attendee scores table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-navy">Attendee Scores</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[400px]">
              <thead>
                <tr className="border-b border-slate-150 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500 hidden sm:table-cell">Company</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Score</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {attendees.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-400 text-sm">No attendees yet</td>
                  </tr>
                ) : (
                  attendees.map((a) => (
                    <tr key={a.id} className="border-b border-slate-150 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-navy">
                        {a.student.firstName} {a.student.lastName}
                      </td>
                      <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{a.student.company ?? '—'}</td>
                      <td className="px-4 py-3">
                        {a.quizScore !== null ? (
                          <span className={cn('font-semibold', a.quizScore >= passingScore ? 'text-green-600' : 'text-red-500')}>
                            {a.quizScore}%
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <AttendeeStatusBadge status={a.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
